import { css } from '@emotion/react';
import { faSliders } from '@fortawesome/pro-solid-svg-icons';
import { useCallback } from 'react';
import {
  EmptyStateComponent, EmptyStateSize,
} from '~/_shared/components/emptyState/emptyState.component';
import {
  ScrollBarComponent, ScrollbarType,
} from '~/_shared/components/scrollbar/scrollbar.component';
import { useTranslation } from '~/_shared/utils/hooks';
import { useGetColumnName } from '~/_shared/utils/spreadsheet/useColumnName';
import { DEFAULT_METRIC_VALUE_WEIGHT } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.helpers';
import { type MetricValue } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.types';
import { DataWeightsItemComponent } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/dataWeightsItem.component';

const LINE_SIZE = 54;
const METRICS_PADDING = 16;
const METRICS_GAP = 20;
const MAX_HEIGHT = 180;

const containerStyle = css({
  maxHeight: MAX_HEIGHT,
  minHeight: LINE_SIZE,
  overflow: 'hidden',
});

const contentStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: METRICS_GAP,
  padding: METRICS_PADDING,
});

type DataWeightsProps = {
  dataMetricsValues: ReadonlyArray<MetricValue>;
  demographicsValues: ReadonlyArray<MetricValue>;
  maxWeight: number;
  minWeight: number;
  onChangeDataMetrics: (dataMetricsValues: ReadonlyArray<MetricValue>) => void;
  onChangeDemographics: (demographicsValues: ReadonlyArray<MetricValue>) => void;
};

export const DataWeightsComponent = ({
  dataMetricsValues,
  onChangeDataMetrics,
  demographicsValues,
  onChangeDemographics,
  minWeight,
  maxWeight,
}: DataWeightsProps) => {
  const [t] = useTranslation();
  const getColumnName = useGetColumnName();

  const onChangeDataMetricsSlider = useCallback((id: string, weight: number, groupId?: string) => {
    const newMetricValues = dataMetricsValues.map(dataMetricsValue => {
      if (isMetricMatch(dataMetricsValue, id, groupId)) {
        return ({
          ...dataMetricsValue,
          weight,
        });
      }

      return dataMetricsValue;
    });
    onChangeDataMetrics(newMetricValues);
  }, [onChangeDataMetrics, dataMetricsValues]);

  const onChangeDemographicsSlider = useCallback((id: string, weight: number) => {
    const newMetricValues = demographicsValues.map(demographicsValue => {
      if (isMetricMatch(demographicsValue, id)) {
        return ({
          ...demographicsValue,
          weight,
        });
      }
      return demographicsValue;
    });

    onChangeDemographics(newMetricValues);
  }, [onChangeDemographics, demographicsValues]);

  const uncheckMetric = (metricsValues: ReadonlyArray<MetricValue>, id: string, groupId?: string) => {
    return metricsValues.map(metricValue => {
      if (isMetricMatch(metricValue, id, groupId)) {
        return {
          ...metricValue,
          checked: false,
          weight: DEFAULT_METRIC_VALUE_WEIGHT,
        };
      }

      return metricValue;
    });
  };

  const handleRemoveDataMetric = useCallback((id: string, groupId?: string) =>
    onChangeDataMetrics(uncheckMetric(dataMetricsValues, id, groupId)),
  [onChangeDataMetrics, dataMetricsValues]);

  const handleRemoveDemographic = useCallback((id: string, groupId?: string) =>
    onChangeDemographics(uncheckMetric(demographicsValues, id, groupId)),
  [onChangeDemographics, demographicsValues]);

  const renderSliders = (
    metricsValues: ReadonlyArray<MetricValue>,
    onChange: (id: string, weight: number, groupId?: string) => void,
    onRemove: (id: string, groupId?: string) => void,
  ) => {
    return metricsValues
      .filter(metricsValue => metricsValue.checked)
      .map(metricsValue => {
        const columnName = getColumnName(metricsValue.id);
        const weightMetric = metricsValue.name ? metricsValue : {
          ...metricsValue,
          name: `${t('Empty')} | ${columnName}`,
        };

        return (
          <DataWeightsItemComponent
            key={`${metricsValue.id}-${metricsValue.groupId}`}
            metric={weightMetric}
            onChange={(newMetric) => onChange(newMetric.id, newMetric.weight, newMetric.groupId)}
            onRemove={() => onRemove(metricsValue.id, metricsValue.groupId)}
            minWeight={minWeight}
            maxWeight={maxWeight}
          />
        );
      });
  };

  const numberOfMetrics = dataMetricsValues.concat(demographicsValues).filter(v => v.checked).length;

  return (
    <div css={containerStyle} >
      {numberOfMetrics ? (
        <ScrollBarComponent
          type={ScrollbarType.Vertical}
          maxHeight={MAX_HEIGHT}
        >
          <div css={contentStyle}>
            {renderSliders(dataMetricsValues, onChangeDataMetricsSlider, handleRemoveDataMetric)}
            {renderSliders(demographicsValues, onChangeDemographicsSlider, handleRemoveDemographic)}
          </div>
        </ScrollBarComponent>
      ) : (
        <EmptyStateComponent
          css={{ padding: 10 }}
          icon={faSliders}
          title={t('No Metrics Selected')}
          size={EmptyStateSize.Small}
        />
      )}
    </div>
  );
};

const isMetricMatch = (metric: MetricValue, id: string, groupId?: string) =>
  metric.id === id && metric.groupId === groupId;
